import { Button, DatePicker, Dropdown, Radio, Space } from 'antd';
import { collect } from 'collect.js';
import React, { useEffect, useMemo, useState } from 'react';
import { CalendarOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { Stringable } from '../../services/utils/Stringable';

const { RangePicker } = DatePicker;

export enum Variant {
  Dropdown = 'dropdown',
  RadioGroup = 'radio_group',
}

export enum Period {
  Today = 'today',
  Yesterday = 'yesterday',
  ThisWeek = 'this_week',
  LastWeek = 'last_week',
  ThisMonth = 'this_month',
  LastMonth = 'last_month',
  All = 'all',
}

export type DateRange = {
  start_date: Moment;
  end_date: Moment;
};

type BaseFilter = {
  id: string;
  name: string;
};

type PeriodFilter = BaseFilter & {
  period: Period;
  start_date?: never;
  end_date?: never;
};

type DateRangeFilter = BaseFilter &
  DateRange & {
    period?: never;
  };

export type Filter = PeriodFilter | DateRangeFilter;

type Props = {
  variant?: Variant;
  defaultFilter?: string;
  customFilters?: Filter[];
  excludeFilterIds?: Period[];
  onChange: (value: Pick<Filter, 'period' | 'start_date' | 'end_date'>) => void;
};

export default function DateRangePicker({
  onChange,
  variant = Variant.RadioGroup,
  defaultFilter = null,
  customFilters = [],
  excludeFilterIds = [],
}: Props) {
  const filters = useMemo(
    () =>
      collect(Object.values(Period))
        .map((item: Period) => ({
          id: item,
          name: Stringable.toHuman(item),
          ...dateRangeFactory(item),
        }))
        .merge(customFilters)
        .whereNotIn('id', excludeFilterIds),
    [customFilters, excludeFilterIds],
  );

  const [selectedFilter, setSelectedFilter] = useState(filters.firstWhere('id', defaultFilter));
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    if (dateRange) {
      onChange({
        start_date: dateRange[0].startOf('day'),
        end_date: dateRange[1].endOf('day'),
      });

      return;
    }

    selectedFilter && onChange(selectedFilter);
  }, [selectedFilter, dateRange]);

  const onSelect = (id: string) => {
    setSelectedFilter(filters.firstWhere('id', id));
  };

  function toggleCustomFilter() {
    setShowDateRangePicker(false);
    setDateRange(null);
  }

  const renderVariant = {
    [Variant.Dropdown]: () => (
      <Dropdown
        menu={{
          // @ts-expect-error wrong ts for dropdown items
          items: filters.map((filter) => ({ key: filter.id, label: filter.name })),
          selectable: true,
          selectedKeys: selectedFilter ? [selectedFilter.id] : null,
          onSelect: (e) => onSelect(e.key),
        }}
      >
        <Button>
          <Space>
            {selectedFilter?.name ?? 'Select period'}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    ),
    [Variant.RadioGroup]: () => (
      <Radio.Group value={selectedFilter?.id} onChange={(e) => onSelect(e.target.value)}>
        {filters.map((filter) => (
          <Radio.Button key={filter.id} value={filter.id} className="px-xs">
            {filter.name}
          </Radio.Button>
        ))}
      </Radio.Group>
    ),
  }[variant];

  return (
    <div className="flex items-center gap-xs">
      {!showDateRangePicker && (
        <>
          {renderVariant()}
          <Button icon={<CalendarOutlined />} onClick={() => setShowDateRangePicker(true)} />
        </>
      )}
      {showDateRangePicker && (
        <>
          <RangePicker format="DD.MM.YYYY" value={dateRange} onChange={setDateRange} defaultOpen />
          <Button icon={<CloseOutlined />} onClick={toggleCustomFilter} />
        </>
      )}
    </div>
  );
}

export const dateRangeFactory = (period: Period): DateRange =>
  ({
    [Period.Today]: {
      start_date: moment().startOf('day'),
      end_date: moment().endOf('day'),
    },
    [Period.Yesterday]: {
      start_date: moment().subtract(1, 'day').startOf('day'),
      end_date: moment().subtract(1, 'day').endOf('day'),
    },
    [Period.ThisWeek]: {
      start_date: moment().startOf('week'),
      end_date: moment().endOf('week'),
    },
    [Period.LastWeek]: {
      start_date: moment().subtract(1, 'week').startOf('week'),
      end_date: moment().subtract(1, 'week').endOf('week'),
    },
    [Period.ThisMonth]: {
      start_date: moment().startOf('month'),
      end_date: moment().endOf('month'),
    },
    [Period.LastMonth]: {
      start_date: moment().subtract(1, 'month').startOf('month'),
      end_date: moment().subtract(1, 'month').endOf('month'),
    },
    [Period.All]: {
      start_date: null,
      end_date: null,
    },
  })[period];
